import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

const API_BASE_URL = "https://api.itrustsecure.com";

export default function InvoiceTrackPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}/invoices/track/${token}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  async function confirmReceipt() {
    if (!window.confirm("Confirm you received this order? The seller will receive their payment.")) return;
    setConfirming(true);
    try {
      const res = await fetch(`${API_BASE_URL}/invoices/track/${token}/confirm`, { method: "PUT", headers: { "Content-Type": "application/json" } });
      if (res.ok) setConfirmed(true);
      else { const d = await res.json(); window.alert(d.error || "Could not confirm"); }
    } catch { window.alert("Something went wrong"); }
    finally { setConfirming(false); }
  }

  const s = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "#0a0f1e", color: "#f0f4ff", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
    card: { background: "#0d1526", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 24, padding: 36, width: "100%", maxWidth: 480 },
    logo: { fontSize: 22, fontWeight: 800, color: "#fff", marginBottom: 28, textAlign: "center" },
    logoAccent: { color: "#3b82f6" },
    title: { fontSize: 22, fontWeight: 800, marginBottom: 6 },
    sub: { fontSize: 13, color: "rgba(255,255,255,0.4)", marginBottom: 24 },
    infoBox: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 20, marginBottom: 16 },
    row: { display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: 8 },
    label: { color: "rgba(255,255,255,0.4)" },
    value: { fontWeight: 600 },
    amount: { fontSize: 32, fontWeight: 800, color: "#3b82f6", marginBottom: 4 },
    statusBadge: (color) => ({ display: "inline-block", padding: "6px 14px", borderRadius: 999, background: `${color}20`, color, border: `1px solid ${color}40`, fontSize: 13, fontWeight: 700, marginBottom: 20 }),
    confirmBtn: { width: "100%", background: "#16a34a", color: "#fff", border: "none", padding: "14px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 15, marginBottom: 10 },
    warningBox: { background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.2)", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "#d97706", marginBottom: 16 },
  };

  if (loading) return <div style={s.root}><div style={{ color: "rgba(255,255,255,0.5)" }}>Loading order...</div></div>;

  if (!data || data.error) return (
    <div style={s.root}>
      <div style={s.card}>
        <div style={s.logo}>i<span style={s.logoAccent}>Trust Secure</span></div>
        <div style={{ textAlign: "center", color: "rgba(255,255,255,0.5)" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Order not found</div>
        </div>
      </div>
    </div>
  );

  const order = data.order;
  const invoice = data.invoice;
  const buyerName = data.guestName || data.buyer?.fullName || "Unknown";
  const isCompleted = order?.status === "COMPLETED" || confirmed;
  const isShipped = order?.sellerConfirmed;
  const statusColor = isCompleted ? "#16a34a" : isShipped ? "#7c3aed" : "#d97706";
  const statusLabel = isCompleted ? "✅ Completed" : isShipped ? "📦 Shipped — Awaiting Confirmation" : "⏳ Awaiting Shipment";

  return (
    <div style={s.root}>
      <div style={s.card}>
        <div style={s.logo}>i<span style={s.logoAccent}>Trust Secure</span></div>
        <div style={s.title}>Order Tracking</div>
        <div style={s.sub}>Hi {buyerName} — here's your order status</div>

        <span style={s.statusBadge(statusColor)}>{statusLabel}</span>

        <div style={s.infoBox}>
          <div style={s.row}><span style={s.label}>Item</span><span style={s.value}>{invoice?.title}</span></div>
          <div style={s.row}><span style={s.label}>Seller</span><span style={s.value}>{invoice?.seller?.fullName}</span></div>
          <div style={s.row}><span style={s.label}>Payment</span><span style={{ ...s.value, color: "#16a34a" }}>✅ Paid</span></div>
          {order?.deliveryProofs?.[0]?.trackingNumber && (
            <div style={s.row}>
              <span style={s.label}>Tracking #</span>
              <span style={{ ...s.value, color: "#3b82f6" }}>{order.deliveryProofs[0].trackingNumber}</span>
            </div>
          )}
          <div style={s.amount}>${Number(order?.totalAmount || 0).toFixed(2)}</div>
        </div>

        {isShipped && !isCompleted && (
          <>
            <div style={s.warningBox}>
              ⚠️ Only confirm receipt if you have received the item as described. Once confirmed, payment will be released to the seller.
            </div>
            <button style={{ ...s.confirmBtn, opacity: confirming ? 0.5 : 1 }} onClick={confirmReceipt} disabled={confirming}>
              {confirming ? "Confirming..." : "✅ Yes, I Received This Order"}
            </button>
          </>
        )}

        {isCompleted && (
          <div style={{ background: "rgba(22,163,74,0.1)", border: "1px solid rgba(22,163,74,0.3)", borderRadius: 12, padding: 16, textAlign: "center", color: "#4ade80", fontWeight: 700 }}>
            ✅ Order Complete — Thank you!
          </div>
        )}

        {!isShipped && !isCompleted && (
          <div style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 12, padding: 16, fontSize: 13, color: "#60a5fa" }}>
            ⏳ Waiting for the seller to ship your order. You'll receive an email when it ships.
          </div>
        )}

        <div style={{ marginTop: 20, fontSize: 11, color: "rgba(255,255,255,0.3)", textAlign: "center" }}>
          Bookmark this page to track your order anytime
        </div>
      </div>
    </div>
  );
}